import { Solution } from '@energiebespaarders/constants';
import { Image } from '@energiebespaarders/symbols';
import { Medium } from '@energiebespaarders/symbols/helpers';
import {
  Bat,
  ChatAlertAlt,
  ClipboardCheck,
  FileEuro,
  Shield,
  WrenchScrewdriver,
} from '@energiebespaarders/symbols/icons/solid';
import Natuurkalender from '../components/Natuurkalender';
import type { QuoteInfoAccordion } from '../utils';
import { debWarranty, insulationCertificationWarranty } from './constants';
import NatuurvriendelijkIsoleren from '/public/img/envCustomer/natuurvriendelijkisoleren.png';

interface WallInsulationWarrantyDeterminants {
  seamState: string | null; // 'good' | 'bad' | 'mediocre'
  moistureSpots: boolean | null;
  crawlspaceWetness: string | null; // 'standingWater' | 'veryWet' | 'damp' | 'dry'
}

export const wallinsulationWarrantyProvisions = (
  determinants: WallInsulationWarrantyDeterminants,
) => {
  const provisions = [];
  if (determinants.seamState === 'mediocre' || determinants.seamState === 'bad') {
    provisions.push({
      title: 'Er is slecht of matig voegwerk aanwezig',
      warning:
        'Dit kan leiden tot vochtproblemen in de woning. Wij adviseren u om het voegwerk te laten herstellen. Opdrachtgever vrijwaart Energiebespaarders voor problemen ontstaan door scheurvorming, vocht- of vorstschade in het metsel- en/of voegwerk en gevolgen van de algehele conditie van het metsel- en/of voegwerk in combinatie met de isolatiewerkzaamheden.',
    });
  }

  if (determinants.moistureSpots) {
    provisions.push({
      title: 'Er zijn zichtbare vochtplekken aanwezig op de gevel',
      warning:
        'Energiebespaarders biedt geen garantie op problemen ontstaan door de vocht of oorzaak van vocht na het aanbrengen van de spouwmuurisolatie. Opdrachtgever erkent de risico’s en vrijwaart Energiebespaarders van de gevolgen van de isolatiewerkzaamheden.',
    });
  }

  const wetCrawlspaceWarning =
    'Energiebespaarders biedt geen garantie op problemen ontstaan door het aanbrengen van isolatiemateriaal in de kruipruimte indien de fundering voorafgaand aan de werkzaamheden niet goed en deugdelijk geïnjecteerd is.';
  if (determinants.crawlspaceWetness === 'standingWater') {
    provisions.push({
      title: 'Er staat een laag water in de kruipruimte',
      warning: wetCrawlspaceWarning,
    });
  }
  if (determinants.crawlspaceWetness === 'veryWet') {
    provisions.push({ title: 'De kruipruimte is erg vochtig', warning: wetCrawlspaceWarning });
  }
  if (determinants.crawlspaceWetness === 'damp') {
    provisions.push({ title: 'De kruipruimte is vochtig', warning: wetCrawlspaceWarning });
  }

  return provisions;
};

export const wallInsulationInfoAccordions = (
  needsPtc: boolean,
  certification: 'insula' | 'skg' = 'skg',
  needsPrepayment: boolean,
): QuoteInfoAccordion[] => [
  {
    key: 'natureFriendly',
    title: 'Gedragscsode natuurvriendelijk isoleren',
    paragraphs: [
      <Image
        key="natureFriendly-img"
        src={NatuurvriendelijkIsoleren}
        alt="Natuurvriendelijk Isoleren logo"
        width="120px"
      />,
      <p key="natureFriendly-1">
        Bij natuurvriendelijk isoleren kan het hele jaar door worden geïsoleerd, als de woning
        vooraf 'natuurvrij' is gemaakt. Dat wil zeggen dat de vogels of vleermuizen vooraf
        vertrokken zijn uit de spouw of onder het dak. Isolatiebedrijven mogen de woning niet het
        gehele jaar door natuurvrij maken, daarbij werken ze volgens de natuurkalender: het
        isolatiebedrijf mag tijdens de winterrust wel het natuurvrij maken voorbereiden (flappen
        ophangen), maar niet isoleren. Tijdens de kraamperiode van de vleermuis en het broedseizoen
        van vogels mag het isolatiebedrijf niet natuurvrij maken.
      </p>,
      <p key="natureFriendly-2">
        Het is daarom belangrijk om tijdig een afspraak te maken. Als uw woning vóór 1 april of vóór
        1 november natuurvrij is gemaakt, mag er ook in het voorjaar en/of winter geïsoleerd worden.
        Het vraagt een iets andere manier van plannen, maar de isolatie kan dan wel gewoon doorgaan.
        Uw isolatiebedrijf kan u daarover adviseren.
      </p>,
      <p key="natureFriendly-3">
        Je kunt hier meer over lezen op de website van{' '}
        <a
          href="https://www.natuurvriendelijkisoleren.nl/"
          title="Natuurvriendelijk isoleren"
          rel="noopener noreferrer"
        >
          Natuurvriendelijk Isoleren
        </a>
        .
      </p>,
      <Natuurkalender key="natureFriendly-3" />,
    ],
    icon: Bat,
    hide: false,
  },
  {
    key: 'feasibility',
    title: 'Technische haalbaarheid en volledigheid',
    paragraphs: [
      'Deze offerte is geldig onder voorbehoud van technische haalbaarheid en volledigheid. Dit betekent dat er een technische controle nodig is, waarna de nog mutaties kunnen plaatsvinden, afhankelijk van eventuele complicaties en/of meerwerk.',
      'De opdracht is voorgelegd aan een van onze installateurs. Die zal na akkoord bepalen of er nog een technische controle nodig is voordat de installatie kan worden gepland. Bij spouwmuurisolatie wordt bijvoorbeeld gekeken of er vervuiling in de spouw zit.',
    ],
    icon: ClipboardCheck,
    hide: !needsPtc,
  },
  {
    key: 'warranty',
    title: 'Installatiegarantie en service',
    paragraphs: [
      insulationCertificationWarranty(Solution.WallInsulation, certification),
      debWarranty,
    ],
    icon: Shield,
  },
  {
    key: 'invoicing',
    title: 'Facturatie',
    paragraphs: [
      ...(needsPrepayment
        ? [
            <p key="wall-invoicing-0">
              <Medium>Aanbetaling:</Medium> 25% van het totale offertebedrag wordt na het accepteren
              van de definitieve offerte {needsPtc ? '(dus na de technische controle) ' : ''}in
              rekening gebracht.
            </p>,
            <p key="wall-invoicing-1">
              <Medium>Facturatie:</Medium> 75% van het totale offertebedrag na oplevering.
            </p>,
          ]
        : [
            <p key="wall-invoicing-1">
              <Medium>Facturatie:</Medium> het totale offertebedrag na oplevering.
            </p>,
          ]),
      <p key="wall-invoicing-2">
        <Medium>Betalingstermijn:</Medium> binnen 7 dagen na factuurdatum.
      </p>,
      <p key="wall-invoicing-3">
        <Medium>Uitvoering:</Medium> in overleg, na ontvangst opdracht. Als u de opdracht wilt
        annuleren, zijn hier mogelijk kosten aan verbonden.
      </p>,
    ],
    icon: FileEuro,
  },

  {
    key: 'activities',
    title: 'Montage en coördinatie',
    paragraphs: [
      'De werkzaamheden bij het isoleren van de spouwmuur omvatten in hoofdzaak:',
      <ul key="wall-activities-1">
        <li>Het boren van de gaten in de buitenmuur;</li>
        <li>Het vullen van de spouwmuur met het isolatiemateriaal;</li>
        <li>
          Het netjes vullen van de geboorde gaten met standaard voegspecie, zoveel mogelijk in
          dezelfde kleur als de voegen;
        </li>
        <li>Het plaatsen van ventilatieroosters, mits apart vermeld in de offerte;</li>
        <li>Het plaatsen van spouwborstels, indien noodzakelijk en vermeld op de offerte;</li>
        <li>Het netjes achterlaten van de werkplek.</li>
      </ul>,
      <p key="wall-activities-2">
        Indien niet anders vermeld, zijn de volgende werkzaamheden{' '}
        <Medium>niet in deze offerte opgenomen</Medium>:
      </p>,
      <ul key="wall-activities-3">
        <li>Het vrij toegankelijk maken van de gevels;</li>
        <li>Het reinigen of restaureren van de gevels.</li>
      </ul>,
    ],
    icon: WrenchScrewdriver,
  },
  {
    key: 'reminders',
    title: 'Let ook hierop',
    paragraphs: [
      'Er zijn een aantal aandachtspuntjes voor u als bewoner om de uitvoer zo soepel mogelijk te laten verlopen:',
      <ul key="wall-reminders-1">
        <li>De bewoner draagt zorg voor een vrije parkeerplaats bij de woning;</li>
        <li>
          De bewoner draagt zorg voor een goede bereikbaarheid van de te behandelen oppervlakte.
          Eventuele bosschages, bouwkundige belemmeringen, plantenbakken en/of opgeslagen voorraden
          dienen verwijderd te worden, zodat de uitvoerders vrije toegang hebben tot de te
          behandelen oppervlakte.
        </li>
      </ul>,
    ],
    icon: ChatAlertAlt,
  },
];
